import { useState, useEffect } from 'react';
import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { TextInputComponent } from 'baseComponents/TextInputComponent';
import { SelectComponent } from 'baseComponents/SelectComponent';
import { Spin, Empty } from 'antd';
import './AdminGlobalConfigurationForm.scss';
import { SetGlobalApiConfigurationParams } from 'hooks/useSetGlobalApiConfiguration';

export type AdminGlobalConfigurationRow = { key: string; value: string };

export type AdminGlobalConfigurationFormProps = {
  data: AdminGlobalConfigurationRow[] | null;
  onSave: (rows: SetGlobalApiConfigurationParams) => void;
  isLoading?: boolean;
};

export const AdminGlobalConfigurationForm = ({
  data,
  onSave,
  isLoading,
}: AdminGlobalConfigurationFormProps) => {
  const [editValues, setEditValues] = useState(
    () => data?.map((row) => row.value) || []
  );
  const [touched, setTouched] = useState(() => data?.map(() => false) || []);

  useEffect(() => {
    if (data) {
      setEditValues(data.map((row) => row.value));
      setTouched(data.map(() => false));
    }
  }, [data]);

  const handleChange = (idx: number, newValue: string) => {
    setEditValues((values) => values.map((v, i) => (i === idx ? newValue : v)));
    setTouched((t) => t.map((v, i) => (i === idx ? true : v)));
  };

  const isBooleanField = (key: string) =>
    key === 'audience_exclude_all_channels';

  const booleanOptions = [
    { label: 'True', value: '1' },
    { label: 'False', value: '0' },
  ];

  return isLoading ? (
    <div className='admin-global-config-table__loader'>
      <Spin size='large' />
    </div>
  ) : (
    <table className='admin-global-config-table'>
      <thead>
        <tr>
          <th className='admin-global-config-table__header'>Key</th>
          <th className='admin-global-config-table__header'>Value</th>
          <th
            className='admin-global-config-table__header'
            style={{ width: 80 }}
          >
            Action
          </th>
        </tr>
      </thead>
      <tbody>
        {!data || data.length === 0 ? (
          <tr>
            <td colSpan={3} style={{ textAlign: 'center', padding: 32 }}>
              <Empty description='No configuration data available' />
            </td>
          </tr>
        ) : (
          data.map((row, idx) => (
            <tr key={row.key}>
              <td className='admin-global-config-table__cell'>
                {row.key
                  .replace(/_/g, ' ')
                  .replace(/^./, (c) => c.toUpperCase())}
              </td>
              <td className='admin-global-config-table__cell'>
                {isBooleanField(row.key) ? (
                  <SelectComponent
                    options={booleanOptions}
                    value={editValues[idx]}
                    onChange={(value) => handleChange(idx, value)}
                    placeholder='Select value'
                  />
                ) : (
                  <TextInputComponent
                    value={editValues[idx]}
                    onChange={(e) => handleChange(idx, e.target.value)}
                  />
                )}
              </td>
              <td
                className='admin-global-config-table__cell admin-global-config-table__action'
                style={{ width: 80 }}
              >
                <ButtonComponent
                  label='Save'
                  appereance='primary'
                  size='small'
                  disabled={!touched[idx] || editValues[idx] === row.value}
                  onClick={() => {
                    if (!data) return;

                    // Create payload with all current values
                    const payload = data.reduce((acc, row, idx) => {
                      const currentValue = editValues[idx] || row.value;

                      if (isBooleanField(row.key)) {
                        // Convert "1" to true, "0" to false for boolean fields
                        (acc as any)[row.key] = currentValue === '1';
                      } else {
                        // Keep as number for numeric fields
                        (acc as any)[row.key] = Number(currentValue);
                      }
                      return acc;
                    }, {} as SetGlobalApiConfigurationParams);

                    onSave(payload);

                    setTouched((t) => t.map((v, i) => (i === idx ? false : v)));
                  }}
                />
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};
